import { getIncomeChildSupports } from './mapIncomeTotals/childSupport.adapter';
import { getIncomeWithRange } from './mapIncomeTotals/getIncomeWithRange.adapter';
import { getJobTotals } from './mapIncomeTotals/jobs.adapter';
import { getIncome } from './mapIncomeTotals/income.adapter';
import { WAGES_AND_INCOME_SECTION_NAMES } from 'pages/AffordaWagesAndIncome/constants';
import { WAGES_AND_INCOME_COMMON_FIELD_NAMES } from 'pages/AffordaWagesAndIncome/constants.fields';
import { CHOICE_NAMES } from 'pages/AffordaWagesAndIncome/constants.choices';
import { getTotalByFieldName } from './mapIncomeTotals/incomeTotals.utils';
import parseToWagesStructure from './utils.parseToWagesStructure';

export const mapIncomeTotals = (data) => {
  // Get all unique memberIds from all income sources
  const allMemberIds = new Set();

  // Helper to add memberIds from an array of records
  const addMemberIds = (records) => {
    if (records?.length) {
      records.forEach((record) => {
        if (record?.memberId) {
          allMemberIds.add(record.memberId);
        }
      });
    }
  };

  // Collect all memberIds
  addMemberIds(data.jobs);
  addMemberIds(data.anticipatedJobs);
  addMemberIds(data.childSupports);
  addMemberIds(data.otherIncomes);
  addMemberIds(data.socialSecs);
  addMemberIds(data.alimonies);
  addMemberIds(data.trusts);
  addMemberIds(data.pensions);
  addMemberIds(data.vaBenefits);
  addMemberIds(data.publicAids);
  addMemberIds(data.onlines);
  addMemberIds(data.unemployments);
  addMemberIds(data.gifts);
  addMemberIds(data.benefitsRental);
  const memberIds = [...allMemberIds];

  // Process all income types, checking choices first
  const jobsResult =
    data.choices?.[CHOICE_NAMES.CURRENT_JOB] === 'yes'
      ? getJobTotals(data.jobs)
      : { jobs: [], totalIncome: 0 };

  const anticipatedJobsResult =
    data.choices?.[CHOICE_NAMES.ANTICIPATE_JOB_12_MONTHS] === 'yes'
      ? getJobTotals(data.anticipatedJobs)
      : { jobs: [], totalIncome: 0 };

  const childSupportsResult =
    data.choices?.[CHOICE_NAMES.CHILD_SUPPORT_ELIGIBLE] === 'yes'
      ? getIncomeChildSupports(data.childSupports)
      : { childSupports: [], totalIncome: 0 };

  const otherIncomesResult =
    data.choices?.[CHOICE_NAMES.PAYMENTS_OTHER] === 'yes'
      ? getIncomeWithRange(
          WAGES_AND_INCOME_SECTION_NAMES.OTHER_INCOMES,
          data.otherIncomes,
        )
      : { otherIncomes: [], totalIncome: 0 };

  const socialSecsResult =
    data.choices?.[CHOICE_NAMES.SSI] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.SOCIAL_SECS,
          'socialSecurity',
          data.socialSecs,
        )
      : { socialSecs: [], totalIncome: 0 };

  const alimoniesResult =
    data.choices?.[CHOICE_NAMES.ALIMONY_PAYMENTS] === 'yes'
      ? getIncomeWithRange(
          WAGES_AND_INCOME_SECTION_NAMES.ALIMONIES,
          data.alimonies,
        )
      : { alimonies: [], totalIncome: 0 };

  const giftsResult =
    data.choices?.[CHOICE_NAMES.BENEFITS_FAMILY] === 'yes'
      ? getIncomeWithRange(WAGES_AND_INCOME_SECTION_NAMES.GIFTS, data.gifts)
      : { gifts: [], totalIncome: 0 };

  const trustsResult =
    data.choices?.[CHOICE_NAMES.PAYMENTS_TRUST] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.TRUSTS,
          WAGES_AND_INCOME_COMMON_FIELD_NAMES.source,
          data.trusts,
        )
      : { trusts: [], totalIncome: 0 };

  const pensionsResult =
    data.choices?.[CHOICE_NAMES.BENEFITS_PENSION] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.PENSIONS,
          WAGES_AND_INCOME_COMMON_FIELD_NAMES.source,
          data.pensions,
        )
      : { pensions: [], totalIncome: 0 };

  const vaBenefitsResult =
    data.choices?.[CHOICE_NAMES.BENEFITS_VA] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.VA_BENEFITS,
          'vaBenefits',
          data.vaBenefits,
        )
      : { vaBenefits: [], totalIncome: 0 };

  const publicAidsResult =
    data.choices?.[CHOICE_NAMES.BENEFITS_SNAPS] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.PUBLIC_ASSISTANCES,
          WAGES_AND_INCOME_COMMON_FIELD_NAMES.source,
          data.publicAids,
        )
      : { publicAids: [], totalIncome: 0 };

  const onlinesResult =
    data.choices?.[CHOICE_NAMES.PAYMENTS_ONLINE] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.ONLINES,
          WAGES_AND_INCOME_COMMON_FIELD_NAMES.source,
          data.onlines,
        )
      : { onlines: [], totalIncome: 0 };

  const unemploymentsResult =
    data.choices?.[CHOICE_NAMES.BENEFITS_DISABILITY] === 'yes'
      ? getIncome(
          WAGES_AND_INCOME_SECTION_NAMES.UNEMPLOYMENTS,
          WAGES_AND_INCOME_COMMON_FIELD_NAMES.source,
          data.unemployments,
        )
      : { unemployments: [], totalIncome: 0 };
  // Map results by memberId
  const memberIncomes = memberIds.map((memberId) => {
    // Helper to get member's income items and total from a result
    const getMemberIncome = (result, field, extraFields = []) => {
      const memberData = result?.[field]?.find(
        (item) => item.memberId === memberId,
      );
      const memberIncomesResult = {
        items: memberData?.[field] || [],
        total: memberData?.totalIncome || 0,
      };
      if (extraFields?.length > 0) {
        extraFields.forEach((extraField) => {
          memberIncomesResult[extraField] = memberData?.[extraField];
        });
      }

      return memberIncomesResult;
    };

    // Get all income types for this member
    const jobs = getMemberIncome(
      jobsResult,
      WAGES_AND_INCOME_SECTION_NAMES.CURRENT_JOBS,
    );
    const anticipatedJobs = getMemberIncome(
      anticipatedJobsResult,
      WAGES_AND_INCOME_SECTION_NAMES.CURRENT_JOBS,
    );
    const childSupports = getMemberIncome(
      childSupportsResult,
      WAGES_AND_INCOME_SECTION_NAMES.CHILD_SUPPORTS,
      ['childSupportValues'],
    );
    const otherIncomes = getMemberIncome(
      otherIncomesResult,
      WAGES_AND_INCOME_SECTION_NAMES.OTHER_INCOMES,
    );
    const socialSecs = getMemberIncome(
      socialSecsResult,
      WAGES_AND_INCOME_SECTION_NAMES.SOCIAL_SECS,
    );
    const alimonies = getMemberIncome(
      alimoniesResult,
      WAGES_AND_INCOME_SECTION_NAMES.ALIMONIES,
    );
    const gifts = getMemberIncome(
      giftsResult,
      WAGES_AND_INCOME_SECTION_NAMES.GIFTS,
    );
    const trusts = getMemberIncome(
      trustsResult,
      WAGES_AND_INCOME_SECTION_NAMES.TRUSTS,
    );
    const pensions = getMemberIncome(
      pensionsResult,
      WAGES_AND_INCOME_SECTION_NAMES.PENSIONS,
    );
    const vaBenefits = getMemberIncome(
      vaBenefitsResult,
      WAGES_AND_INCOME_SECTION_NAMES.VA_BENEFITS,
    );
    const publicAids = getMemberIncome(
      publicAidsResult,
      WAGES_AND_INCOME_SECTION_NAMES.PUBLIC_ASSISTANCES,
    );
    const onlines = getMemberIncome(
      onlinesResult,
      WAGES_AND_INCOME_SECTION_NAMES.ONLINES,
    );
    const unemployments = getMemberIncome(
      unemploymentsResult,
      WAGES_AND_INCOME_SECTION_NAMES.UNEMPLOYMENTS,
    );
    // ... get other income types
    const totals = {
      alimonies: alimonies.total,
      anticipatedJobs: anticipatedJobs.total,
      childSupports: childSupports.total,
      gifts: gifts.total,
      jobs: jobs.total,
      onlines: onlines.total,
      otherIncomes: otherIncomes.total,
      pensions: pensions.total,
      vaBenefits: vaBenefits.total,
      publicAids: publicAids.total,
      socialSecs: socialSecs.total,
      trusts: trusts.total,
      unemployments: unemployments.total,
    };

    // Calculate total income for this member
    const totalIncome = Object.values(totals).reduce(
      (sum, value) => sum + value,
      0,
    );

    const publicHousing =
      data.choices?.[CHOICE_NAMES.BENEFITS_RENTAL] === 'yes' &&
      data.benefitsRental?.length > 0 &&
      !!data?.benefitsRental?.find((item) => item.memberId === memberId);

    return {
      memberId,
      totalIncome,
      jobs: jobs.items,
      anticipatedJobs: anticipatedJobs.items,
      childSupports: childSupports.items,
      childSupportValues: childSupports.childSupportValues,
      otherIncomes: otherIncomes.items,
      socialSecs: socialSecs.items,
      alimonies: alimonies.items,
      gifts: gifts.items,
      trusts: trusts.items,
      pensions: pensions.items,
      vaBenefits: vaBenefits.items,
      publicAids: publicAids.items,
      onlines: onlines.items,
      unemployments: unemployments.items,
      publicHousing: publicHousing,
      totals,
    };
  });

  // Calculate grand total from all member totals
  const grandTotal = getTotalByFieldName(memberIncomes, 'totalIncome');

  // Parse member incomes to wages structure
  const memberWagesStructure = parseToWagesStructure(memberIncomes);

  const memberTotals = memberIncomes.map((member) => ({
    memberId: member.memberId,
    totalIncome: member.totalIncome,
  }));

  return {
    totalIncome: grandTotal,
    memberTotals,
    memberIncomes: memberWagesStructure,
    memberData: memberIncomes,
  };
};
